import { CircularProgress, Typography } from '@mui/material'
import { BlogMajor } from '@shopify/polaris-icons'
import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { SignContractAgreementModal } from 'src/components/SignContractAgreementModal'
import { DocusignFieldsFormModal } from 'src/components/docusign-field-form-modal/DocusignFieldsFormModal'
import { ClientSecureWrapper } from 'src/components/layout/client/ClientSecureWrapper'
import { Button } from 'src/components/shared/Button'
import {
  FetchDocusignSigningEnvelopeOptions,
  DocusignCompleteSigning,
  DocusignDeclineSigning,
  DocusignStartSigning,
  FetchDriver,
} from 'src/constants/actionTypes'
import { CLIENT_ROUTES } from 'src/constants/routes'
import { PartnerNameType, partnerAgreementContentMap } from 'src/constants/signContract'
import {
  DocusignSigningEnvelopeOptionsResponse,
  DocusignSigningField,
  DocusignStartSigningResponse,
} from 'src/interfaces/docusign'
import { getIsTokenSignIn } from 'src/selectors/auth'
import { getPartner } from 'src/selectors/partner'
import { useQuery } from 'src/utils/hooks/useQuery'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { PendingReturnStepSource, savePendingReturnStep } from 'src/utils/pendingReturnStep'
import { captureSentryError } from 'src/utils/sentry'
import { showToast } from 'src/utils/toast'
import tw from 'twin.macro'
import { StepLabels } from 'src/components/client/step-labels/StepLabels'

const DOCUSIGN_CONTAINER_ID = 'docusign-container'

interface SigningField {
  tabLabel: string
  value: string
}

interface Props {
  currentStepId: string
}

const SignContractComponent = ({ currentStepId }: Props) => {
  const dispatch = useDispatch()
  const { getQuery } = useQuery()
  const navigate = useNavigate()

  const partner = useSelector(getPartner)
  const isIsTokenSignIn = useSelector(getIsTokenSignIn)

  const [docusignSigningData, setDocusignSigningData] =
    useState<DocusignStartSigningResponse | null>(null)
  const [signingEnvelopeOptions, setSigningEnvelopeOptions] =
    useState<DocusignSigningEnvelopeOptionsResponse | null>(null)
  const [isStartLoading, setIsStartLoading] = useState<boolean>(false)
  const [isSigningFallbackLoading, setIsSigningFallbackLoading] = useState<boolean>(false)
  const [isFieldsModalOpen, setIsFieldsModalOpen] = useState<boolean>(false)
  const [isAgreementModalOpen, setIsAgreementModalOpen] = useState<boolean>(false)
  const [modalFields, setModalFields] = useState<Array<DocusignSigningField>>([])
  const [modalHiddenFields, setModalHiddenFields] = useState<Array<DocusignSigningField>>([])
  const [signingIframeCrashed, setSigningIframeCrashed] = useState<boolean>(false)
  const [isSigningReady, setIsSigningReady] = useState<boolean>(false)

  const signingIframeExistsCheckIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const signingIframeWaitObserverRef = useRef<{
    observer: MutationObserver
    timeoutId: NodeJS.Timeout
  } | null>(null)
  const signingReadyTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const docusignReturnEvent = getQuery('event')

  useEffect(() => {
    if (!docusignReturnEvent) {
      return
    }

    if (docusignReturnEvent === 'signing_complete') {
      navigate(CLIENT_ROUTES.DOCS_URL)

      handleCompleteSigning()
    }
  }, [docusignReturnEvent])

  const agreementContent = useMemo(
    () =>
      partner?.name ? partnerAgreementContentMap[partner?.name as PartnerNameType] : undefined,
    [partner?.name],
  )

  useEffect(() => {
    const load = async () => {
      try {
        const response: DocusignSigningEnvelopeOptionsResponse | undefined =
          await createAsyncAction(
            dispatch,
            FetchDocusignSigningEnvelopeOptions.request({ stepId: currentStepId }),
          )

        if (!response) {
          throw new Error('Signing envelope options are not available')
        }

        setSigningEnvelopeOptions(response)
      } catch (err) {
        // ignore this error
      }
    }

    load()
  }, [currentStepId])

  const handleStartSigning = useCallback(
    async (signingFields?: Array<SigningField>) => {
      setIsStartLoading(true)
      setSigningIframeCrashed(false)

      try {
        const signingResponse: DocusignStartSigningResponse | undefined = await createAsyncAction(
          dispatch,
          DocusignStartSigning.request({
            signingFields,
            hiddenFields: modalHiddenFields,
            stepId: currentStepId,
            embedded: true,
          }),
        )

        if (!signingResponse) {
          throw new Error('Signing is not available')
        }

        if (signingResponse.retryFetchDriver) {
          await createAsyncAction(dispatch, FetchDriver.request())

          return
        }

        if (signingResponse.signingFields?.length) {
          setModalFields(signingResponse.signingFields)
          setModalHiddenFields(signingResponse.hiddenFields || [])
          setIsFieldsModalOpen(true)
          setIsStartLoading(false)

          return
        }

        setDocusignSigningData(signingResponse)
        handleLoadDocusign(signingResponse)
      } catch (err) {
        captureSentryError(err, {
          message: '[SignContract(client).handleStartSigning]',
        })

        showToast('Failed to load signing page, please try again later', {
          variant: 'error',
        })
      } finally {
        setIsStartLoading(false)
      }
    },
    [currentStepId, modalHiddenFields],
  )

  const handleStartFallbackSigning = useCallback(async () => {
    setIsSigningFallbackLoading(true)

    const newTab = window.open('', '_blank')

    try {
      const signingResponse: DocusignStartSigningResponse | undefined = await createAsyncAction(
        dispatch,
        DocusignStartSigning.request({
          stepId: currentStepId,
          embedded: false,
        }),
      )

      if (!signingResponse) {
        throw new Error('Signing is not available')
      }

      if (signingResponse.retryFetchDriver) {
        newTab?.close()
        await createAsyncAction(dispatch, FetchDriver.request())

        return
      }

      if (signingResponse.signingData?.url && newTab) {
        savePendingReturnStep(PendingReturnStepSource.Docusign, currentStepId)
        newTab.location.href = signingResponse.signingData.url
      } else {
        newTab?.close()
        throw new Error('Signing is not available')
      }
    } catch (err) {
      newTab?.close()
      captureSentryError(err, {
        message: '[SignContract(client).handleStartFallbackSigning]',
      })

      showToast('Failed to load signing page, please try again later', {
        variant: 'error',
      })
    } finally {
      setIsSigningFallbackLoading(false)
    }
  }, [currentStepId])

  // Any iframe in our container: the SDK empties the container before mounting the signing iframe,
  // so it is the only one there, whatever id DocuSign gives it (plain `js-library-iframe` in older
  // SDK versions, `js-library-iframe-<instanceId>` now). Not a document-wide id lookup, because the
  // SDK also renders hidden `js-library-iframe-preload-*` iframes into <body>.
  const findDocusignIframe = useCallback((): HTMLElement | null => {
    return document.querySelector<HTMLElement>(`#${DOCUSIGN_CONTAINER_ID} iframe`)
  }, [])

  const clearSigningTimers = useCallback(() => {
    if (signingIframeWaitObserverRef.current) {
      clearTimeout(signingIframeWaitObserverRef.current.timeoutId)
      signingIframeWaitObserverRef.current.observer.disconnect()
      signingIframeWaitObserverRef.current = null
    }

    if (signingIframeExistsCheckIntervalRef.current) {
      clearInterval(signingIframeExistsCheckIntervalRef.current)
      signingIframeExistsCheckIntervalRef.current = null
    }

    if (signingReadyTimeoutRef.current) {
      clearTimeout(signingReadyTimeoutRef.current)
      signingReadyTimeoutRef.current = null
    }
  }, [])

  // Load progress is tracked by the SDK `ready` event (see handleLoadDocusign), not the iframe
  // `load` event: `load` waits for every DocuSign sub-resource and routinely takes over 10s on
  // slow connections while the signing UI is already usable.
  const startSigningIframeListeners = useCallback(() => {
    const IFRAME_WAIT_TIMEOUT_MS = 5000
    const IFRAME_EXISTS_CHECK_INTERVAL_MS = 10000

    const attachListeners = (iframe: HTMLElement) => {
      iframe.addEventListener('error', () => {
        setSigningIframeCrashed(true)

        captureSentryError(new Error('Docusign iframe is crashed'), {
          message: '[startSigningIframeListeners]',
        })
      })

      signingIframeExistsCheckIntervalRef.current = setInterval(() => {
        if (!findDocusignIframe()) {
          setSigningIframeCrashed(true)

          captureSentryError(new Error('Docusign iframe is not found'), {
            message: '[startSigningIframeListeners]',
          })
        }
      }, IFRAME_EXISTS_CHECK_INTERVAL_MS)
    }

    const existingIframe = findDocusignIframe()

    if (existingIframe) {
      attachListeners(existingIframe)

      return
    }

    const timeoutId = setTimeout(() => {
      signingIframeWaitObserverRef.current?.observer.disconnect()
      signingIframeWaitObserverRef.current = null
      setSigningIframeCrashed(true)

      captureSentryError(new Error('Docusign iframe is not found'), {
        message: '[startSigningIframeListeners]',
      })
    }, IFRAME_WAIT_TIMEOUT_MS)

    const observer = new MutationObserver(() => {
      const iframe = findDocusignIframe()

      if (iframe) {
        clearTimeout(timeoutId)
        observer.disconnect()
        signingIframeWaitObserverRef.current = null
        attachListeners(iframe)
      }
    })

    signingIframeWaitObserverRef.current = { observer, timeoutId }

    observer.observe(document.body, { childList: true, subtree: true })
  }, [findDocusignIframe])

  const handleCompleteSigning = useCallback(async () => {
    try {
      await createAsyncAction(dispatch, DocusignCompleteSigning.request({ stepId: currentStepId }))
    } catch (err) {
      showToast('Failed to finish signing, please try again later', {
        variant: 'error',
      })
    }

    await createAsyncAction(dispatch, FetchDriver.request())
  }, [currentStepId])

  const handleDeclineSigning = useCallback(async () => {
    try {
      await createAsyncAction(dispatch, DocusignDeclineSigning.request({ stepId: currentStepId }))
      await createAsyncAction(dispatch, FetchDriver.request())
    } catch (err) {
      // ignore this error
    }
  }, [currentStepId])

  const handleLoadDocusign = useCallback(
    async (payload: DocusignStartSigningResponse) => {
      if (!payload.signingData?.url) {
        return
      }

      try {
        if (!window.DocuSign) {
          throw new Error('DocuSign SDK is not loaded')
        }

        const docusign = await window.DocuSign.loadDocuSign(
          process.env.REACT_APP_DOCUSIGN_INTEGRATION_KEY as string,
        )

        const signing = docusign.signing({
          url: payload.signingData.url,
          displayFormat: 'focused',
          style: {
            branding: {
              primaryButton: {
                backgroundColor: '#333',
                color: '#fff',
              },
            },
            signingNavigationButton: {
              finishText: 'Continue',
              position: 'bottom-center',
            },
          },
        })

        signing.on('ready', () => {
          setIsStartLoading(false)
          setIsSigningReady(true)

          if (signingReadyTimeoutRef.current) {
            clearTimeout(signingReadyTimeoutRef.current)
            signingReadyTimeoutRef.current = null
          }
        })

        signing.on('sessionEnd', async (event) => {
          clearSigningTimers()

          if (event.sessionEndType === 'signing_complete') {
            handleCompleteSigning()

            return
          }

          if (event.sessionEndType === 'ttl_expired') {
            setDocusignSigningData(null)
            setIsSigningReady(false)
            showToast('Signing link is expired, please start signing again', {
              variant: 'error',
            })

            return
          }

          if (event.sessionEndType === 'decline') {
            await handleDeclineSigning()
            setDocusignSigningData(null)
            setIsSigningReady(false)
            showToast('Signing is declined, please start signing again or contact support', {
              variant: 'error',
            })

            return
          }
        })

        signing.mount(`#${DOCUSIGN_CONTAINER_ID}`)

        const SIGNING_READY_TIMEOUT_MS = 30000

        signingReadyTimeoutRef.current = setTimeout(() => {
          if (signingReadyTimeoutRef.current) {
            signingReadyTimeoutRef.current = null
            setSigningIframeCrashed(true)

            captureSentryError(new Error('Docusign signing ready event timed out'), {
              message: '[handleLoadDocusign]',
            })
          }
        }, SIGNING_READY_TIMEOUT_MS)

        startSigningIframeListeners()
      } catch (err: any) {
        setIsStartLoading(false)
        setDocusignSigningData(null)
        setSigningIframeCrashed(true)
        captureSentryError(err, {
          message: '[handleLoadDocusign]',
        })
      }
    },
    [clearSigningTimers, handleCompleteSigning, handleDeclineSigning, startSigningIframeListeners],
  )

  useEffect(() => {
    if (!signingIframeCrashed) {
      return
    }

    setDocusignSigningData(null)
    setIsStartLoading(false)
    clearSigningTimers()

    showToast('Failed to start signing, please try again', {
      variant: 'error',
    })
  }, [signingIframeCrashed, clearSigningTimers])

  useEffect(() => {
    return () => {
      clearSigningTimers()
    }
  }, [clearSigningTimers])

  const handleCloseFieldsModal = useCallback(() => {
    setIsFieldsModalOpen(false)
  }, [])

  const handleSubmitFieldsModal = useCallback(
    async (signingFields?: Array<SigningField>) => {
      await handleStartSigning(signingFields)

      setIsFieldsModalOpen(false)
    },
    [handleStartSigning],
  )

  const handleStartSigningPress = useCallback(() => {
    if (agreementContent) {
      setIsAgreementModalOpen(true)

      return
    }

    handleStartSigning()
  }, [agreementContent, handleStartSigning])

  const handleSubmitAgreement = useCallback(() => {
    setIsAgreementModalOpen(false)
    handleStartSigning()
  }, [handleStartSigning])

  return (
    <ClientSecureWrapper
      footer={
        docusignSigningData ? null : (
          <Fragment>
            {signingIframeCrashed ? (
              <Typography css={tw`mb-4 text-shades-400`} variant="body2">
                The signing window couldn't load. Please try signing in a new tab instead.
              </Typography>
            ) : null}
            {!isIsTokenSignIn &&
            signingIframeCrashed &&
            signingEnvelopeOptions?.signingEnvelopeAvailable ? (
              <Button
                className={tw`w-full`}
                color="light"
                loading={isSigningFallbackLoading}
                onClick={handleStartFallbackSigning}
                data-testid="contractors-onboarding-sign-contract-fallback-new-tab-btn"
              >
                Sign the contract in a new tab
              </Button>
            ) : null}
            <Button
              className={signingIframeCrashed ? tw`w-full mt-4` : tw`w-full`}
              variant="contained"
              loading={isStartLoading}
              onClick={handleStartSigningPress}
              data-testid="contractors-onboarding-sign-contract-start-btn"
            >
              Sign the contract
            </Button>
            {!isIsTokenSignIn &&
            signingEnvelopeOptions?.signingEnvelopeAvailable &&
            !signingIframeCrashed ? (
              <Button
                className={tw`w-full mt-4`}
                color="light"
                loading={isSigningFallbackLoading}
                onClick={handleStartFallbackSigning}
                data-testid="contractors-onboarding-sign-contract-new-tab-btn"
              >
                Sign the contract in a new tab
              </Button>
            ) : null}
          </Fragment>
        )
      }
    >
      <div
        css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
      >
        <BlogMajor width={20} />
      </div>
      <StepLabels />
      <Typography css={tw`mb-6`} variant="h2">
        Sign the contract
      </Typography>
      <Typography css={tw`mb-12 text-shades-400`}>
        Sign your contracts and relevant documents via Docusign. Tap the button below to begin.
        {signingEnvelopeOptions?.signingEnvelopeAvailable ? (
          <Typography css={tw`mt-4 text-shades-400`}>
            If you are experiencing issues with the signing process, please try{' '}
            <strong>Sign the contract in a new tab</strong> instead.
          </Typography>
        ) : null}
      </Typography>
      {!signingIframeCrashed ? (
        <div
          id={DOCUSIGN_CONTAINER_ID}
          data-testid="contractors-onboarding-sign-contract-docusign-container"
          css={[
            tw`fixed top-0 left-0 h-full w-full bg-shades-0 overflow-auto`,
            docusignSigningData ? undefined : tw`opacity-0 hidden`,
          ]}
        >
          {docusignSigningData && !isSigningReady ? (
            <div css={tw`flex h-full w-full items-center justify-center`}>
              <CircularProgress />
            </div>
          ) : null}
        </div>
      ) : null}
      <DocusignFieldsFormModal
        isOpen={isFieldsModalOpen}
        fields={modalFields}
        onClose={handleCloseFieldsModal}
        onSubmit={handleSubmitFieldsModal}
      />
      <SignContractAgreementModal
        isOpen={isAgreementModalOpen}
        content={agreementContent}
        onClose={() => setIsAgreementModalOpen(false)}
        onSubmit={handleSubmitAgreement}
      />
    </ClientSecureWrapper>
  )
}

export const SignContract = memo(SignContractComponent)
